"use client";
import { useAuthStore } from "@/store/useAuthStore";
import type { Mentor } from "@/hooks/useMentorFilters";
import axios from "axios";
import useSWR from "swr";

const API = process.env.NEXT_PUBLIC_API_BASE_URL;

// Raw API record: tolerates both camelCase and snake_case availability flags.
interface RawMentor {
  isAvailableForHire?: boolean;
  is_available_for_hire?: boolean;
  [key: string]: unknown;
}

const publicFetcher = (url: string) =>
  axios.get(url).then((r) => r.data);

const authFetcher = ([url, token]: [string, string]) =>
  axios.get(url, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);

export function useMentors() {
  const { accessToken, id } = useAuthStore();

  const {
    data: mentorsData,
    isLoading: mentorsLoading,
    error: mentorsError,
    mutate: mutateMentors,
  } = useSWR(`${API}/mentors`, publicFetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 30000,
  });

  const { data: leaderboardData, isLoading: leaderboardLoading } = useSWR(
    `${API}/mentors/leaderboard`,
    publicFetcher,
    { revalidateOnFocus: false, dedupingInterval: 30000 }
  );

  const { data: applicationStatus, mutate: mutateStatus } = useSWR(
    id && accessToken ? [`${API}/mentors/application/status`, accessToken] : null,
    authFetcher,
    { revalidateOnFocus: true, dedupingInterval: 10000 }
  );

  const applyAsMentor = async (data: {
    displayName: string;
    bio: string;
    qualifications: string;
    countriesOfExpertise: string[];
    fieldsOfStudy: string[];
    linkedinUrl?: string;
    avatar?: string;
    expertise?: string[];
    servicesOffered?: string[];
    isAvailableForHire?: boolean;
    hourlyRate?: number;
  }) => {
    const res = await axios.post(`${API}/mentors/apply`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    mutateStatus();
    // The backend flips user.accountType to MENTOR on successful apply, but
    // the persisted Zustand store still has the old value. Refresh it
    // immediately so MentorGuard and other guards see the new role on this
    // session — otherwise the user has to refresh manually.
    useAuthStore.setState({ accountType: "MENTOR", mentorStatus: "PENDING" });
    return res.data;
  };

  const getApplicationStatus = async () => {
    const res = await axios.get(`${API}/mentors/application/status`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return res.data;
  };

  const rawMentors = (mentorsData?.data ?? mentorsData) as
    | RawMentor[]
    | undefined;
  const normalizedMentors = rawMentors?.map((m) => ({
    ...m,
    isAvailableForHire: m.isAvailableForHire ?? m.is_available_for_hire,
  }));

  return {
    mentors: normalizedMentors as Mentor[] | undefined,
    leaderboard: (leaderboardData?.data ?? leaderboardData) as
      | Mentor[]
      | undefined,
    applicationStatus,
    loading: mentorsLoading || leaderboardLoading,
    error: mentorsError,
    mutateMentors,
    applyAsMentor,
    getApplicationStatus,
    mutateStatus,
  };
}
